import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {PullRequestErrorState} from '../components/PullRequestErrorState'
import {pullRequestsChangesRoute, pullRequestsChangesWithRangeRoute} from './files-changed-routes'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsLayoutRoute} from './layout-route'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useCallback, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef} from 'react'
import {useQueryClient} from '@github-ui/react-query'
import type {PullRequest} from '../types'
import type {FilesRoutePayload} from './route-payload-types'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {usePullRequestAnalytics} from '../hooks/use-pull-request-analytics'
import {useMemoryStats} from '@github-ui/use-memory-stats'
import {useWebVitals} from '@github-ui/use-web-vitals'
import {sendCustomMetric} from '@github-ui/stats'
import {getCurrentDiffImplementation} from '../helpers/get-diff-implementation'
import {DiffComparisonViewer} from '../components/DiffComparisonViewer'
import {
  fileMetadataQueryKey,
  TANSTACK_MEMORY_HIT,
  useFileMetadata,
  type FileMetadataCacheResult,
} from '../page-data/loaders/use-file-metadata'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  mergeFileMetadataIntoChangesData,
  reportFileMetadataError,
  type FileMetadataErrorReason,
} from '../page-data/loaders/merge-file-metadata'
import type {FileMetadataResponse} from '@github-ui/dotcom-schema/types/pull_requests/v1/file_metadata'
import {ChangesLoadTimingContext, useChangesLoadTiming} from '../hooks/use-changes-load-timing'

interface FilesAnalyticsProps {
  viewSettings: FilesRoutePayload['user']['viewSettings']
}

export function ChangesEntrypoint() {
  return (
    <ErrorBoundary
      critical
      boundaryName="ChangesEntrypoint"
      fallback={<PullRequestErrorState text="Changes cannot be loaded" />}
    >
      <Changes />
    </ErrorBoundary>
  )
}

export function ChangesWithRangeEntrypoint() {
  return (
    <ErrorBoundary
      critical
      boundaryName="ChangesWithRangeEntrypoint"
      fallback={<PullRequestErrorState text="Changes cannot be loaded" />}
    >
      <ChangesWithRange />
    </ErrorBoundary>
  )
}

function Changes() {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsChangesRoute, 'mainQuery')
  const cacheSplitEnabled = isFeatureEnabled('prs_files_changed_cache_split_routes')
  const loadTimingEnabled = isFeatureEnabled('prs_files_changed_load_timing')
  const queryClient = useQueryClient()
  const {baseOid, headOid} = routeData.pullRequest.comparison
  const queryArguments = getFileMetadataQueryArguments(pullRequest, routeData)
  const initialCacheResult: FileMetadataCacheResult | undefined =
    cacheSplitEnabled && queryClient.getQueryData(fileMetadataQueryKey(queryArguments)) !== undefined
      ? TANSTACK_MEMORY_HIT
      : undefined
  const {recordCacheResult: recordHPCCacheResult} = useChangesHPCTiming(cacheSplitEnabled, initialCacheResult)
  const filesChangedLoadTiming = useChangesLoadTiming(
    loadTimingEnabled && !routeData.virtualizeDiffEntries
      ? routeData.isSingleFileMode
        ? 'single'
        : 'progressive'
      : undefined,
    cacheSplitEnabled,
    initialCacheResult,
  )
  const recordCacheResult = useCallback(
    (result: FileMetadataCacheResult) => {
      recordHPCCacheResult(result)
      filesChangedLoadTiming?.recordCacheResult(result)
    },
    [recordHPCCacheResult, filesChangedLoadTiming],
  )

  const content = cacheSplitEnabled ? (
    <ChangesWithMetadata
      pullRequest={pullRequest}
      routeData={routeData}
      onCacheResult={recordCacheResult}
      onMetadataSettled={filesChangedLoadTiming?.recordMetadataSettled}
    />
  ) : (
    <FilesChangedComponent
      pullRequest={pullRequest}
      routeData={routeData}
      // force remount when routeData updates after soft navigation so the component gets fresh data
      key={`${baseOid}-${headOid}`}
    />
  )

  return filesChangedLoadTiming ? (
    <ChangesLoadTimingContext value={filesChangedLoadTiming}>{content}</ChangesLoadTimingContext>
  ) : (
    content
  )
}

function ChangesWithMetadata({
  pullRequest,
  routeData,
  onCacheResult,
  onMetadataSettled,
}: {
  pullRequest: PullRequest
  routeData: FilesRoutePayload
  onCacheResult: (result: FileMetadataCacheResult) => void
  onMetadataSettled?: () => void
}) {
  const {baseOid, headOid} = routeData.pullRequest.comparison
  const metadataQuery = useFileMetadata(getFileMetadataQueryArguments(pullRequest, routeData), onCacheResult)

  useLayoutEffect(() => {
    if (!metadataQuery.isPending) onMetadataSettled?.()
  }, [metadataQuery.isPending, onMetadataSettled])

  return (
    <ErrorBoundary
      key={`${baseOid}-${headOid}`}
      boundaryName="ChangesFileMetadata"
      fallback={<FilesChangedComponent pullRequest={pullRequest} routeData={routeData} />}
    >
      <FilesChangedWithMetadata pullRequest={pullRequest} routeData={routeData} metadata={metadataQuery.data} />
    </ErrorBoundary>
  )
}

function getFileMetadataQueryArguments(pullRequest: PullRequest, routeData: FilesRoutePayload) {
  const {baseOid, headOid} = routeData.pullRequest.comparison

  return {
    owner: routeData.repository.ownerLogin,
    repo: routeData.repository.name,
    pullRequestNumber: String(pullRequest.number),
    baseOid,
    headOid,
    hideWhitespace: routeData.user.viewSettings.hideWhitespace,
    // Fallback data when the file metadata response omits its payload.
    routeData,
  }
}

function FilesChangedWithMetadata({
  pullRequest,
  routeData,
  metadata,
}: {
  pullRequest: PullRequest
  routeData: FilesRoutePayload
  metadata: FileMetadataResponse | undefined
}) {
  const {routeData: completeRouteData, errorReasons} = useMemo(
    () =>
      metadata
        ? mergeFileMetadataIntoChangesData(routeData, metadata)
        : {routeData, errorReasons: [] as FileMetadataErrorReason[]},
    [metadata, routeData],
  )
  const reportedReasonsRef = useRef(new Set<FileMetadataErrorReason>())
  const {baseOid, headOid} = routeData.pullRequest.comparison

  useEffect(() => {
    for (const reason of new Set(errorReasons)) {
      if (reportedReasonsRef.current.has(reason)) continue

      reportedReasonsRef.current.add(reason)
      reportFileMetadataError(reason)
    }
  }, [errorReasons])

  return <FilesChangedComponent key={`${baseOid}-${headOid}`} pullRequest={pullRequest} routeData={completeRouteData} />
}

function useChangesHPCTiming(cacheSplitEnabled: boolean, initialCacheResult?: FileMetadataCacheResult) {
  const hpcRef = useRef<number | undefined>(undefined)
  const cacheResultRef = useRef<FileMetadataCacheResult | undefined>(initialCacheResult)
  const hasReportedRef = useRef(false)

  const reportIfReady = useCallback(() => {
    const hpc = hpcRef.current
    const cacheResult = cacheResultRef.current

    if (hasReportedRef.current || hpc === undefined || (cacheSplitEnabled && cacheResult === undefined)) {
      return
    }

    hasReportedRef.current = true

    sendCustomMetric({
      name: 'PULL_REQUESTS_FILES_CHANGED_HPC',
      value: hpc,
      tags: {
        cacheSplit: cacheSplitEnabled ? 'enabled' : 'disabled',
        persistenceResult: cacheResult ?? 'not_applicable',
      },
    })
  }, [cacheSplitEnabled])

  useWebVitals('hpc', event => {
    if (event.detail.hpc === undefined) return

    hpcRef.current = event.detail.hpc
    reportIfReady()
  })

  const recordCacheResult = useCallback(
    (result: FileMetadataCacheResult) => {
      cacheResultRef.current = result
      reportIfReady()
    },
    [reportIfReady],
  )

  return {recordCacheResult}
}

function ChangesWithRange() {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsChangesWithRangeRoute, 'mainQuery')
  const {range} = useRouteParams(pullRequestsChangesWithRangeRoute)

  return <FilesChangedComponent pullRequest={pullRequest} routeData={routeData} key={range} />
}

export function FilesAnalytics({viewSettings}: FilesAnalyticsProps) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const sendPageView = useEffectEvent(() => {
    sendPullRequestAnalyticsEvent('page_view.files_page', 'PAGE_VIEW', {
      browser_width: typeof window !== 'undefined' ? window.innerWidth : 0,
      diff_view_setting: viewSettings.splitPreference,
      comments_setting: viewSettings.commentsPreference,
      line_spacing_setting: viewSettings.lineSpacing,
      change_groups_setting: viewSettings.showChangeGroups ? 'show' : 'hide',
    })
  })

  useEffect(() => {
    sendPageView()
  }, [])

  return null
}

export function FilesChangedComponent({
  pullRequest,
  routeData,
}: {
  pullRequest: PullRequest
  routeData: FilesRoutePayload
}) {
  const diffImplementation = getCurrentDiffImplementation(routeData.virtualizeDiffEntries)

  useMemoryStats({
    statName: 'PULL_REQUESTS_FILES_JS_HEAP',
    tags: {
      version: diffImplementation.version,
      virtualized: String(diffImplementation.virtualized),
    },
  })

  return (
    <AnalyticsProvider
      appName="pull-request-files"
      category="files_tab"
      metadata={{
        pull_request_id: pullRequest.id.toString(),
        base_sha: routeData.pullRequest.comparison.baseOid,
        head_sha: routeData.pullRequest.comparison.headOid,
        diff_version: diffImplementation.version,
        diff_virtualized: String(diffImplementation.virtualized),
      }}
    >
      <FilesAnalytics viewSettings={routeData.user.viewSettings} />
      <DiffComparisonViewer.Viewer
        basePath={pullRequest.pathName}
        initialData={routeData}
        useAdvancedToolbar
        useChangeGroups
        useInlineComments
        useEditOptions
        useFileTree
      />
    </AnalyticsProvider>
  )
}
